import { ReactElement, useEffect, useMemo } from 'react';
import {
    UIErrorState,
    UILoadingState,
    UISubSidebar,
    UISubSidebarGroupProps,
    UISubSidebarItemProps,
    useUINav
} from '@/ui';
import { Outlet } from 'react-router';
import { useFetchMe } from '@/me/queries';
import { AccountSettingsProvider } from './provider';
import { useNullableCompanyContext } from '@/companies/providers';
import { useScrollToTopOnNavigate } from '@/common/hooks';
import { usePagesTranslation } from '../hooks';

export function AccountPage(): ReactElement {
    const { t } = usePagesTranslation();
    // This element outlives the pages inside it, so it keeps its scroll
    // position across a navigation unless it is sent back to the top.
    const scrollRef = useScrollToTopOnNavigate<HTMLDivElement>();

    const { setParts } = useUINav();
    useEffect(() => {
        setParts([
            {
                text: t('shared.labels.account')
            }
        ]);
    }, [setParts, t]);

    const { status, data, refetch } = useFetchMe();

    const nullableCompanyContext = useNullableCompanyContext();

    const absolutePath = `/account`;
    const sideBarItems: UISubSidebarItemProps[] = useMemo(() => {
        const items: UISubSidebarItemProps[] = [
            {
                text: t('shared.labels.account_details'),
                link: 'details',
                additionalActiveLinkPaths: ['/account']
            },
            {
                text: t('shared.labels.email_address'),
                link: 'email'
            },
            {
                text: t('shared.labels.emergency_information'),
                link: 'emergency-information'
            },
            {
                text: t('shared.labels.phone'),
                link: 'phone'
            },
            {
                text: t('shared.labels.security'),
                link: 'security'
            }
        ];

        return items;
    }, [t]);

    const groups: UISubSidebarGroupProps[] = useMemo(() => {
        const groups: UISubSidebarGroupProps[] = [];

        groups.push({
            title: 'USER SETTINGS',
            items: sideBarItems
        });

        if (nullableCompanyContext.company?.id) {
            const path = `/company/${nullableCompanyContext.company.id}/settings`;
            groups.push({
                title: 'COMPANY SETTINGS',
                items: [
                    {
                        text: t('shared.labels.general_settings'),
                        link: path
                    }
                ]
            });
        }
        return groups;
    }, [sideBarItems, nullableCompanyContext, t]);

    return (
        <div style={{ display: 'flex', flex: 1 }}>
            <UISubSidebar absolutePath={absolutePath} groups={groups} />
            <div
                style={{
                    display: 'flex',
                    flex: 1,
                    position: 'relative'
                }}
            >
                {status === 'pending' && <UILoadingState />}
                {status === 'error' && (
                    <UIErrorState
                        title={t('shared.errors.load_settings')}
                        onRetry={refetch}
                    />
                )}
                {status === 'success' && data && (
                    <AccountSettingsProvider data={data}>
                        <div
                            ref={scrollRef}
                            style={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                right: 0,
                                bottom: 0,
                                overflow: 'auto'
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    maxWidth: 1000
                                }}
                            >
                                <Outlet />
                            </div>
                        </div>
                    </AccountSettingsProvider>
                )}
            </div>
        </div>
    );
}
